import { useEffect } from "react";
import { useUiStore } from "../state/ui-store.js";
import { useTheme } from "../state/use-theme.js";
import { useT } from "../i18n/use-t.js";
import { localeDir } from "../i18n/locales.js";
import type { TKey } from "../i18n/strings.js";
import { Sidebar } from "./Sidebar.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { ChatsView } from "./views/ChatsView.js";
import { ChatRoomView } from "./views/ChatRoomView.js";
import { CharactersView } from "./views/CharactersView.js";
import { GamesView } from "./views/GamesView.js";
import { GalleryView } from "./views/GalleryView.js";
import { LorebooksView } from "./views/LorebooksView.js";
import { HubView } from "./views/HubView.js";
import { RoqeetGuideView } from "./views/RoqeetGuideView.js";
import { SettingsView } from "./views/SettingsView.js";
import { Menu, PanelLeftClose, PanelLeftOpen } from "lucide-react";

/** 뷰 → i18n 제목 키. */
const TITLE_KEY: Record<string, TKey> = {
  chats: "nav.chats",
  characters: "nav.characters",
  games: "nav.games",
  gallery: "nav.gallery",
  lorebooks: "nav.lorebooks",
  hub: "nav.hub",
  guide: "nav.guide",
  settings: "nav.settings",
};

/**
 * Top-level responsive shell (decision #11). One responsive tree, not separate
 * phone/tablet/desktop trees:
 *  - <md: sidebar is an off-canvas drawer toggled by the header button.
 *  - >=md: sidebar is a persistent column beside the main pane.
 * Mobile-first media-query breakpoints only (no container queries — those
 * need Safari 16, but the 6s+ ceiling is Safari 15).
 */
export function App() {
  const view = useUiStore((s) => s.view);
  const activeChatId = useUiStore((s) => s.activeChatId);
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUiStore((s) => s.setSidebarOpen);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebarCollapsed = useUiStore((s) => s.toggleSidebarCollapsed);

  // 설정에 따른 테마 적용(결정 #11/#13).
  useTheme();

  // 언어(결정 #13): 반응형 로케일. 제목 번역 + <html> lang/dir(아랍어 RTL) 반영.
  const { locale, t } = useT();
  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = localeDir(locale);
  }, [locale]);

  const titleFor = (v: string): string => (TITLE_KEY[v] ? t(TITLE_KEY[v]!) : "RoQeet Launcher");

  // 채팅방이 열려 있으면(activeChatId) 대화 섹션에서 채팅방을 전체로 보여준다.
  const inChatRoom = view === "chats" && activeChatId;

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* Backdrop for the mobile drawer. */}
      {sidebarOpen && (
        <button
          aria-label={t("app.sidebarClose")}
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar: drawer on mobile, persistent column on md+ (collapsible). */}
      <aside
        className={[
          "fixed inset-y-0 left-0 z-30 w-72 max-w-[80%] transform bg-bg-soft transition-all duration-200 ease-out",
          "md:static md:z-0 md:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          // md+ 접기: 너비 0 + 내용 클립으로 메인 영역에 공간을 내준다(모바일 드로어와 별개).
          sidebarCollapsed
            ? "md:w-0 md:overflow-hidden md:border-r-0"
            : "md:w-72 md:border-r md:border-line",
        ].join(" ")}
      >
        <Sidebar />
      </aside>

      {/* Main pane. */}
      <main className="flex min-w-0 flex-1 flex-col">
        {inChatRoom ? (
          // 채팅방은 자체 헤더(뒤로가기 포함)를 가지므로 공용 헤더를 생략하고
          // 전체 높이를 사용한다. 크래시는 이 영역 안에 격리(결정 #1).
          <ErrorBoundary label={t("app.chatRoom")} resetKey={activeChatId}>
            <ChatRoomView chatId={activeChatId} />
          </ErrorBoundary>
        ) : (
          <>
            <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
              <button
                aria-label={t("app.menuOpen")}
                className="rounded p-2 text-ink-soft hover:bg-bg-raised md:hidden"
                onClick={toggleSidebar}
              >
                <Menu size={20} aria-hidden="true" />
              </button>
              {/* md+ 전용: 고정 사이드바 접기/펼치기(에디터 공간 확보). */}
              <button
                aria-label={sidebarCollapsed ? t("app.sidebarExpand") : t("app.sidebarCollapse")}
                title={sidebarCollapsed ? t("app.sidebarExpand") : t("app.sidebarCollapse")}
                className="hidden rounded p-2 text-ink-soft hover:bg-bg-raised md:inline-flex"
                onClick={toggleSidebarCollapsed}
              >
                {sidebarCollapsed ? (
                  <PanelLeftOpen size={20} aria-hidden="true" />
                ) : (
                  <PanelLeftClose size={20} aria-hidden="true" />
                )}
              </button>
              <h1 className="text-sm font-semibold text-ink">{titleFor(view)}</h1>
            </header>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {/* 뷰 단위 격리: 한 화면 크래시가 사이드바/다른 화면을 막지 않게(결정 #1). */}
              <ErrorBoundary label={titleFor(view)} resetKey={view}>
                {view === "chats" && <ChatsView />}
                {view === "characters" && <CharactersView />}
                {view === "games" && <GamesView />}
                {view === "gallery" && <GalleryView />}
                {view === "lorebooks" && <LorebooksView />}
                {view === "hub" && <HubView />}
                {view === "guide" && <RoqeetGuideView />}
                {view === "settings" && <SettingsView />}
              </ErrorBoundary>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
